import type { Condition } from '@mockd/shared/types/rule';
import { XIcon } from 'lucide-react';

interface ConditionEditorProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
  matchMode: 'all' | 'any';
  onMatchModeChange: (mode: 'all' | 'any') => void;
}

const TARGET_OPTIONS = [
  { value: 'method', label: 'Method' },
  { value: 'path', label: 'Path' },
  { value: 'header', label: 'Header' },
  { value: 'query', label: 'Query Param' },
  { value: 'body_raw', label: 'Body (Raw)' },
  { value: 'body_json', label: 'Body (JSON Path)' },
  { value: 'body_field', label: 'Body (Field)' },
  { value: 'random', label: 'Random %' },
] as const;

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'matches_regex', label: 'matches regex' },
  { value: 'exists', label: 'exists' },
  { value: 'not_exists', label: 'not exists' },
  { value: 'less_than', label: 'less than' },
] as const;

const NO_VALUE_OPERATORS = ['exists', 'not_exists'];
const NEEDS_KEY_TARGETS = ['header', 'query', 'body_json', 'body_field'];

function getDefaultCondition(): Condition {
  return { target: 'method', operator: 'equals', value: '' };
}

function getOperatorsForTarget(target: string) {
  if (target === 'random') {
    return OPERATOR_OPTIONS.filter((op) => op.value === 'less_than');
  }
  return OPERATOR_OPTIONS.filter((op) => op.value !== 'less_than');
}

export function ConditionEditor({ conditions, onChange, matchMode, onMatchModeChange }: ConditionEditorProps) {
  const handleAdd = () => {
    onChange([...conditions, getDefaultCondition()]);
  };

  const handleUpdate = (index: number, updated: Condition) => {
    const next = [...conditions];
    next[index] = updated;
    onChange(next);
  };

  const handleRemove = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const handleTargetChange = (index: number, target: Condition['target']) => {
    const cond = conditions[index];
    const updated: Condition = { ...cond, target };
    // Reset operator if incompatible
    if (target === 'random') {
      updated.operator = 'less_than';
      updated.key = undefined;
    } else if (cond.target === 'random') {
      updated.operator = 'equals';
    }
    if (!NEEDS_KEY_TARGETS.includes(target)) {
      updated.key = undefined;
    }
    handleUpdate(index, updated);
  };

  return (
    <div className="space-y-2">
      {conditions.length === 0 && (
        <p className="text-sm text-base-content/70 italic">Always matches (no conditions)</p>
      )}

      {conditions.length > 1 && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-base-content/70">Match</span>
          <select
            value={matchMode}
            onChange={(e) => onMatchModeChange(e.target.value as 'all' | 'any')}
            className="select select-xs"
            aria-label="Condition match mode"
          >
            <option value="all">all conditions (AND)</option>
            <option value="any">any condition (OR)</option>
          </select>
        </div>
      )}

      {conditions.map((cond, index) => {
        const operators = getOperatorsForTarget(cond.target);
        const needsKey = NEEDS_KEY_TARGETS.includes(cond.target);
        const needsValue = !NO_VALUE_OPERATORS.includes(cond.operator);
        const isRandom = cond.target === 'random';

        return (
          <div key={index} className="flex items-start gap-2 flex-wrap">
            {index > 0 && (
              <span className="text-xs text-base-content/70 self-center font-medium w-8 text-center">
                {matchMode === 'any' ? 'OR' : 'AND'}
              </span>
            )}
            {index === 0 && conditions.length > 1 && (
              <span className="w-8" />
            )}

            <select
              value={cond.target}
              onChange={(e) => handleTargetChange(index, e.target.value as Condition['target'])}
              className="select select-sm w-36"
            >
              {TARGET_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            {needsKey && (
              <input
                type="text"
                value={cond.key || ''}
                onChange={(e) => handleUpdate(index, { ...cond, key: e.target.value })}
                placeholder={cond.target === 'body_json' ? 'user.role' : cond.target === 'body_field' ? 'field.name' : cond.target === 'header' ? 'Header name' : 'Param name'}
                className="input input-sm w-32"
              />
            )}

            <select
              value={cond.operator}
              onChange={(e) => handleUpdate(index, { ...cond, operator: e.target.value as Condition['operator'] })}
              className="select select-sm w-36"
            >
              {operators.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            {needsValue && (
              <div className="flex items-center gap-1">
                <input
                  type={isRandom ? 'number' : 'text'}
                  value={cond.value || ''}
                  onChange={(e) => handleUpdate(index, { ...cond, value: e.target.value })}
                  placeholder={isRandom ? '30' : 'Value'}
                  min={isRandom ? '0' : undefined}
                  max={isRandom ? '100' : undefined}
                  className="input input-sm flex-1 min-w-[80px]"
                />
                {isRandom && (
                  <span className="text-xs text-base-content/70">% of requests</span>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="btn btn-ghost btn-sm btn-square text-error"
              title="Remove condition"
              aria-label="Remove condition"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={handleAdd}
        className="btn btn-ghost btn-sm"
      >
        + Add Condition
      </button>
    </div>
  );
}
